import {
  AfterViewChecked,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChatbotService } from '../../../../core/services/chatbot.service';
import { AuthService } from '../../../../core/services/auth.service';

export type ChatRole = 'user' | 'assistant';

export type ChatMode = 'staff' | 'patient';

export interface ChatImage {
  url: string;
  caption?: string;
}

export interface ChatMessage {
  id: string;
  role: ChatRole;
  content: string;
  timestamp: Date;
  images?: ChatImage[];
}

@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss',
})
export class ChatComponent implements AfterViewChecked {
  @Input() isOpen: boolean = false;

  @Output() closeChat = new EventEmitter<void>();

  @ViewChild('messagesContainer') private messagesContainer?: ElementRef<HTMLDivElement>;

  private readonly chatbotService = inject(ChatbotService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  // Unique identifier for the current chat session.
  private sessionId = this.generateSessionId();

  messages = signal<ChatMessage[]>([]);
  newMessage = signal<string>('');
  isLoading = signal<boolean>(false);
  chatMode = signal<ChatMode>('staff');

  // Image currently shown in the full-size preview modal (null when closed).
  previewImage = signal<ChatImage | null>(null);

  private shouldScroll = false;

  ngAfterViewChecked(): void {
    if (this.shouldScroll) {
      this.scrollToBottom();
      this.shouldScroll = false;
    }
  }

  onCloseChat(): void {
    this.closeChat.emit();
  }

  /** Opens the clicked chat image in a full-size preview modal. */
  openImagePreview(image: ChatImage): void {
    this.previewImage.set(image);
  }

  /** Closes the image preview modal. */
  closeImagePreview(): void {
    this.previewImage.set(null);
  }

  /**
   * Switches between the staff and patient assistants. Because each mode targets
   * a different backend context, the conversation and session are reset so the
   * new assistant starts fresh.
   */
  setChatMode(mode: ChatMode): void {
    if (this.chatMode() === mode || this.isLoading()) {
      return;
    }
    this.chatMode.set(mode);
    this.sessionId = this.generateSessionId();
    this.messages.set([]);
    this.newMessage.set('');
  }

  onSendMessage(): void {
    const content = this.newMessage().trim();
    if (!content || this.isLoading()) {
      return;
    }

    this.pushMessage('user', content);
    this.newMessage.set('');
    this.askAssistant(content);
  }

  handleKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSendMessage();
    }
  }

  formatTime(timestamp: Date): string {
    return timestamp.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  trackByMessageId(_index: number, message: ChatMessage): string {
    return message.id;
  }

  /**
   * Sends the question to the chatbot backend and appends its answer.
   */
  private askAssistant(question: string): void {
    this.isLoading.set(true);

    const request$ =
      this.chatMode() === 'patient'
        ? this.chatbotService.sendPatientMessage({
            message: question,
            sessionId: this.sessionId,
          })
        : this.chatbotService.sendStaffMessage({
            message: question,
            sessionId: this.sessionId,
            userId: this.authService.currentUser()?.id ?? '',
          });

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        const images = (res.metadata?.matchedImages ?? [])
          .filter((image) => !!image.imageUrl)
          .map((image) => ({ url: image.imageUrl, caption: image.imageCaption }));
        this.pushMessage('assistant', res.response, images);
        this.isLoading.set(false);
      },
      error: () => {
        this.pushMessage(
          'assistant',
          'An error occurred while communicating with the assistant. Please try again.'
        );
        this.isLoading.set(false);
      },
    });
  }

  private pushMessage(role: ChatRole, content: string, images?: ChatImage[]): void {
    const message: ChatMessage = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      role,
      content,
      timestamp: new Date(),
      images: images && images.length > 0 ? images : undefined,
    };
    this.messages.update((messages) => [...messages, message]);
    this.shouldScroll = true;
  }

  private generateSessionId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  private scrollToBottom(): void {
    const container = this.messagesContainer?.nativeElement;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }
}
